import React, { useMemo } from "react";
import { ConfiguratorTreeServerErrorView } from "src/components/views/erp/configurator/configuratorForm/ConfiguratorTreeServerErrorView.tsx";
import { ConfiguratorTreeItemModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeItemModel";
import { VerticalBox } from "src/components/common/box/VerticalBox";
import {
	OnComponentInputChangedParams,
	OnComponentInputChangedResult,
	PartConfiguratorFormProps,
} from "src/components/views/erp/configurator/configuratorForm/PartConfiguratorForm.tsx";
import { RecursiveConfiguratorTreeItem } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/RecursiveConfiguratorTreeItem.tsx";
import { PartConfiguratorFormWithTestViews } from "src/components/views/erp/configurator/configuratorForm/PartConfiguratorFormWithTestViews.tsx";
import {
	ConfiguratorTreeFooter,
	ConfiguratorTreeFooterProps,
} from "src/components/views/erp/configurator/configuratorForm/components/ConfiguratorTreeFooter.tsx";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic";
import { getTreeItemSubConfiguratorComponents } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/treeItemModel/configuratorTreeItemQueries.ts";
import { LoadingBox } from "src/components/common/LoadingBox.tsx";
import { LoggedError } from "src/errorHandling/errorLogging.ts";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";

export interface PartConfiguratorTreeStatelessViewProps {
	configuratorType: PartConfiguratorType;
	onSubmit: () => Promise<unknown>;
	onCancel: () => Promise<unknown>;
	onValidate: () => Promise<unknown>;
	rootItem: ConfiguratorTreeItemModel;
	selectedItem: ConfiguratorTreeItemModel | undefined;
	selectTreeItem: (key: string) => Promise<undefined | "interrupted">;
	toggleTreeItemExpansion: (key: string) => void;
	onComponentInputChanged: (
		treeItemKey: string,
		params: OnComponentInputChangedParams,
	) => Promise<OnComponentInputChangedResult>;
	transformTreeItemComponents: (treeItemKey: string) => Promise<unknown>;
	configuratorInputVersion: number;
	isLoading: boolean;
	hasUnsavedChanges: boolean;
	resetToExistingConfiguration: (() => Promise<unknown>) | undefined;
	resetToDefaultValues: () => Promise<unknown>;
	updateVersions?: () => Promise<unknown>;
	updateSingleVersion?: (key: string) => Promise<unknown>;
	onAddSubConfiguratorClicked: (parentKey: string) => Promise<unknown>;
	deleteSubConfiguratorListItem: (key: string) => Promise<unknown>;
	cloneSubConfiguratorListItem: (key: string) => Promise<unknown>;
	hideFooter?: boolean;
	footerExtraComponents?: ConfiguratorTreeFooterProps["extraComponents"];
	serverErrorCustomContent?: React.ReactNode;
	serverError: LoggedError | undefined;
	readonly?: boolean;
	validationEnabled: boolean;
	configuratorComponentSx?: PartConfiguratorFormProps["configuratorComponentSx"];
	configuratorComponentContextMenu?: PartConfiguratorFormProps["configuratorComponentContextMenu"];
	allowInputErrors: boolean;
}

export const PartConfiguratorTreeStatelessView = (props: PartConfiguratorTreeStatelessViewProps) => {
	const {
		onSubmit,
		onCancel,
		onValidate,
		rootItem,
		selectedItem,
		selectTreeItem,
		toggleTreeItemExpansion,
		configuratorInputVersion,
		hasUnsavedChanges,
		resetToExistingConfiguration,
		resetToDefaultValues,
		updateVersions,
		updateSingleVersion,
		onAddSubConfiguratorClicked,
		deleteSubConfiguratorListItem,
		cloneSubConfiguratorListItem,
		hideFooter,
		footerExtraComponents,
		readonly = false,
		allowInputErrors,
		validationEnabled,
	} = props;

	const rootHasAnySubConfigurators = useMemo(
		() => getTreeItemSubConfiguratorComponents(rootItem).length > 0,
		[rootItem],
	);

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<AavoMosaic
				viewId={"D632746D610D8F9D"}
				layout={{
					type: "row",
					splitPercentage: 30,
					first: {
						type: "panel",
						hidden: !rootHasAnySubConfigurators,
						content: (
							<VerticalBox
								component={"ul"}
								sx={{
									overflow: "auto",
									paddingX: 0,
									flex: 1,
								}}
							>
								<RecursiveConfiguratorTreeItem
									key={rootItem.key}
									treeItemModel={rootItem}
									parentTreeItemModel={undefined}
									depth={0}
									selectedItemKey={selectedItem?.key}
									selectTreeItem={selectTreeItem}
									toggleTreeItemExpansion={toggleTreeItemExpansion}
									onAddSubConfiguratorClicked={onAddSubConfiguratorClicked}
									deleteSubConfiguratorListItem={deleteSubConfiguratorListItem}
									cloneSubConfiguratorListItem={cloneSubConfiguratorListItem}
									updateSingleVersion={updateSingleVersion}
									readonly={readonly}
									allowInputErrors={allowInputErrors}
								/>
							</VerticalBox>
						),
					},
					second: {
						type: "panel",
						content: <MainPanelContent {...props} />,
					},
				}}
			/>
			{!hideFooter && (
				<ConfiguratorTreeFooter
					rootConfiguratorItem={rootItem}
					currentInputVersion={configuratorInputVersion}
					onCancel={onCancel}
					submitForm={onSubmit}
					onValidate={validationEnabled ? onValidate : undefined}
					resetToExistingConfiguration={resetToExistingConfiguration}
					resetToDefaultValues={resetToDefaultValues}
					updateVersions={updateVersions}
					hasUnsavedChanges={hasUnsavedChanges}
					extraComponents={footerExtraComponents}
					readonly={readonly}
				/>
			)}
		</VerticalBox>
	);
};

const MainPanelContent = ({
	rootItem,
	selectedItem,
	onComponentInputChanged,
	isLoading,
	readonly,
	serverError,
	serverErrorCustomContent,
	resetToExistingConfiguration,
	resetToDefaultValues,
	transformTreeItemComponents,
	configuratorComponentContextMenu,
	configuratorComponentSx,
}: PartConfiguratorTreeStatelessViewProps) => {
	if (serverError != null)
		return (
			<ConfiguratorTreeServerErrorView
				configuratorType={rootItem.configuratorType}
				error={serverError}
				reload={
					selectedItem == undefined ? undefined : (
						async () => {
							await transformTreeItemComponents(selectedItem.key);
						}
					)
				}
				resetToExistingConfiguration={resetToExistingConfiguration}
				resetToDefaultValues={resetToDefaultValues}
				customContent={serverErrorCustomContent}
			/>
		);

	if (selectedItem != null)
		return (
			<VerticalBox sx={{ flex: 1, position: "relative" }}>
				{isLoading && <LoadingBox overlay />}
				<PartConfiguratorFormWithTestViews
					key={selectedItem.key}
					configuratorType={selectedItem.configuratorType}
					propertyValues={selectedItem.propertyValues}
					components={selectedItem.components}
					configurationSessionId={selectedItem.configurationSessionId}
					componentsWithDocument={selectedItem.componentsWithDocumentsIds}
					onComponentInputChanged={(params) => onComponentInputChanged(selectedItem.key, params)}
					disabled={readonly}
					configuratorComponentContextMenu={configuratorComponentContextMenu}
					configuratorComponentSx={configuratorComponentSx}
				/>
			</VerticalBox>
		);

	return null;
};
